import React, { useState, useRef, useEffect } from 'react';
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2, AlertCircle, Upload, X } from 'lucide-react';
import ScrollReveal from './ScrollReveal';
import { companyData } from '../data';

export default function Contact() {
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileError, setFileError] = useState(null);

  useEffect(() => {
    document.title = 'Kontakt os | Bust Byg & Renovering ApS';
    window.scrollTo(0, 0);
    return () => {
      document.title = companyData.seo.title;
    };
  }, []);

  // File constraints
  const MAX_FILES = 5;
  const MAX_FILE_SIZE_MB = 10;
  const MAX_TOTAL_SIZE_MB = 20;
  const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

  const handleFileChange = (e) => {
    setFileError(null);
    const files = Array.from(e.target.files || []);
    
    if (selectedFiles.length + files.length > MAX_FILES) {
      setFileError(`Du kan højst vedhæfte ${MAX_FILES} filer i alt.`);
      return;
    }

    let currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    const validFiles = [];

    for (const file of files) {
      // Validate type
      if (!ALLOWED_TYPES.includes(file.type)) {
        setFileError(`Filtypen "${file.name}" understøttes ikke. Tilladte typer: PDF, PNG, JPEG, GIF, WEBP.`);
        return;
      }
      // Validate single file size
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setFileError(`Filen "${file.name}" er for stor. Maksimal størrelse per fil er ${MAX_FILE_SIZE_MB} MB.`);
        return;
      }
      // Validate total size
      if (currentTotalSize + file.size > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
        setFileError(`Den samlede filstørrelse overstiger grænsen på ${MAX_TOTAL_SIZE_MB} MB.`);
        return;
      }

      currentTotalSize += file.size;
      validFiles.push(file);
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    // Reset file input value so the same file can be selected again if removed
    if (e.target) e.target.value = '';
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setFileError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const form = formRef.current;
      const formData = new FormData();

      // Append standard fields
      formData.append('name', form.elements.name.value);
      formData.append('email', form.elements.email.value);
      formData.append('phone', form.elements.phone.value);
      formData.append('message', form.elements.message.value);
      
      // Honeypot field (anti-spam)
      formData.append('website_url', form.elements.website_url.value);

      // Append files
      selectedFiles.forEach((file) => {
        formData.append('attachments', file);
      });

      // Submit to specified endpoint as multipart/form-data
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData,
        // Do NOT set Content-Type header; the browser will automatically set it to multipart/form-data with boundary
      });

      const result = await response.json();

      if (!response.ok || result.status !== 'ok') {
        throw new Error(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }

      setSuccess(true);
      setSelectedFiles([]);
      form.reset();
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || 'Der skete en uventet fejl. Kontroller din internetforbindelse og prøv igen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="py-20 md:py-28 bg-[#161616] border-t border-gray-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Contact Info */}
          <div className="lg:col-span-5 space-y-8">
            <ScrollReveal direction="left" duration={800}>
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white uppercase">
                  {companyData.name}
                </h3>
                
                <div className="divide-y divide-gray-800">
                  {/* Phone */}
                  <a
                    href={`tel:${companyData.phoneRaw}`}
                    className="flex items-start gap-4 group text-gray-300 hover:text-white transition-colors py-6 first:pt-0"
                  >
                    <div className="text-white shrink-0 pt-1">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-lg font-bold mt-1">{companyData.phone}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Ring for et uforpligtende tilbud</p>
                    </div>
                  </a>

                  {/* Email */}
                  <a
                    href={`mailto:${companyData.email}`}
                    className="flex items-start gap-4 group text-gray-300 hover:text-white transition-colors py-6"
                  >
                    <div className="text-white shrink-0 pt-1">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-lg font-bold mt-1 break-all">{companyData.email}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Vi svarer normalt inden for 24 timer</p>
                    </div>
                  </a>

                  {/* Address */}
                  <a
                    href={companyData.addressMapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 group text-gray-300 hover:text-white transition-colors py-6 last:pb-0"
                  >
                    <div className="text-white shrink-0 pt-1">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-lg font-bold mt-1">{companyData.address}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Klik for at se rutevejledning</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Map Placeholder or Trust Banner */}
              <div className="text-left space-y-2 pt-4 border-t border-gray-800">
                <p className="text-sm font-bold text-white">Dækningsområde</p>
                <p className="text-xs text-gray-400">
                  Vi udfører opgaver på hele Sjælland
                </p>
              </div>
            </ScrollReveal>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <ScrollReveal direction="right" duration={800} delay={100}>
              <div className="bg-white border border-gray-200 p-8 rounded-lg shadow-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-6 uppercase">
                  Send os en besked
                </h3>

              {success ? (
                // Success State
                <div className="bg-emerald-500/10 border border-emerald-500/30 p-8 rounded-lg text-center space-y-4">
                  <div className="inline-flex p-3 rounded-full bg-emerald-500/20 text-emerald-600">
                    <CheckCircle2 className="w-10 h-10" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 uppercase">Tak for din henvendelse!</h4>
                  <p className="text-gray-600 text-sm max-w-md mx-auto">
                    Vi har modtaget din besked og vender tilbage til dig hurtigst muligt – normalt inden for 24 timer på hverdage.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="mt-4 bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-none text-xs font-bold uppercase tracking-wider transition-colors"
                  >
                    Send en ny besked
                  </button>
                </div>
              ) : (
                // Form
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                  <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
                    <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke)</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  {/* Error Notification */}
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-md flex items-start gap-3 text-red-600 text-sm">
                      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold">Der skete en fejl</p>
                        <p className="mt-1">{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Form Grid */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-gray-600">
                        Navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder="Dit fulde navn"
                        className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 outline-none transition-colors"
                      />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-gray-600">
                        Telefonnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        placeholder="F.eks. 22 80 00 15"
                        className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 outline-none transition-colors"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-gray-600">
                      E-mailadresse <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder="din@email.dk"
                      className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 outline-none transition-colors"
                    />
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-gray-600">
                      Besked <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows="5"
                      placeholder="Beskriv dit projekt, hvad du ønsker udført, og eventuelle tidsrammer..."
                      className="w-full bg-gray-50 border border-gray-200 focus:border-orange-500 rounded-md px-4 py-3 text-gray-900 text-sm placeholder-gray-400 outline-none transition-colors resize-none"
                    />
                  </div>

                  {/* File Upload Section */}
                  <div className="space-y-3">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block">
                      Vedhæft billeder eller dokumenter (Valgfrit)
                    </label>
                    
                    <div className="relative border-2 border-dashed border-gray-200 hover:border-orange-500/40 rounded-lg p-6 text-center transition-colors cursor-pointer bg-gray-50 group">
                      <input
                        type="file"
                        id="attachments"
                        name="attachments"
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="space-y-2">
                        <Upload className="w-8 h-8 text-gray-400 group-hover:text-orange-500 mx-auto transition-colors" />
                        <p className="text-sm font-semibold text-gray-700">
                          Klik for at vælge filer eller træk dem hertil
                        </p>
                        <p className="text-xs text-gray-500">
                          PDF, PNG, JPEG, GIF, WEBP (Maks 5 filer, maks 10 MB pr. fil, maks 20 MB i alt)
                        </p>
                      </div>
                    </div>

                    {/* File Error */}
                    {fileError && (
                      <p className="text-xs text-red-500 font-semibold flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {fileError}
                      </p>
                    )}

                    {/* Selected Files List */}
                    {selectedFiles.length > 0 && (
                      <div className="space-y-2 pt-2">
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                          Valgte filer ({selectedFiles.length}/{MAX_FILES}):
                        </p>
                        <div className="grid gap-2">
                          {selectedFiles.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-gray-50 border border-gray-200 px-3 py-2 rounded text-xs"
                            >
                              <span className="text-gray-700 font-medium truncate max-w-[80%]">
                                {file.name} <span className="text-gray-500">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                              </span>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                                aria-label="Fjern fil"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white py-4 rounded-none text-base font-bold uppercase tracking-wider transition-all duration-200"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Sender...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Send besked</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
            </ScrollReveal>
          </div>

        </div>
      </div>
    </section>
  );
}
